import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Coins, TrendingUp, Calendar, Package, Sparkles, 
  ChevronRight, Zap, AlertCircle, Crown, Clock,
  Image, Video, Music, Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature icons mapping
const FEATURE_ICONS = {
  'Enhanced Resolution': Image,
  'Background Removal': Image,
  'Smart Fill': Wand2,
  'Pro Enhancement': Image,
  'Style Transfer': Video,
  'Video Enhancement': Video,
  'Advanced Edit': Video,
  'Track Separation': Music,
  'Professional Mastering': Music,
  'Advanced Effects': Music,
  'Voice Synthesis': Music,
  'Audio Processing': Music
};

const CreditDashboard = ({ compact = false }) => {
  const { token, user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [packs, setPacks] = useState({});
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  const fetchCreditData = useCallback(async () => {
    if (!token) return;
    
    try {
      const [balanceRes, packsRes] = await Promise.all([
        axios.get(`${API}/credits/balance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/credits/packs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setBalance(balanceRes.data);
      setPacks(packsRes.data);
    } catch (error) {
      console.error('Failed to fetch credit data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCreditData();
  }, [fetchCreditData]);

  const handlePurchase = async (packId) => {
    if (!token) return;
    
    setPurchasing(packId);
    try {
      const response = await axios.post(
        `${API}/credits/purchase`,
        { pack_id: packId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Purchase failed:', error);
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  };

  const formatResetDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `${diffDays} days`;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-24 bg-zinc-800/50 rounded-xl"></div>
        <div className="h-32 bg-zinc-800/50 rounded-xl"></div>
      </div>
    );
  }

  if (!balance) return null;

  // Calculate usage percentage
  const usagePercent = balance.monthly_credits > 0 
    ? (balance.monthly_used / balance.monthly_credits) * 100 
    : 0;
  
  const isLowCredits = balance.balance < 20 && !balance.is_founder;

  // Compact view for sidebar/dashboard widget
  if (compact) {
    return (
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-primary" />
            <span className="font-semibold">Credits</span>
          </div>
          {balance.is_founder && (
            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full flex items-center gap-1">
              <Crown className="w-3 h-3" />
              Unlimited
            </span>
          )}
        </div>
        
        {!balance.is_founder && (
          <>
            <div className="text-3xl font-bold text-primary">
              {balance.balance.toLocaleString()}
            </div>
            
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-zinc-500">
                <span>Monthly: {balance.monthly_used}/{balance.monthly_credits}</span>
                <span>Resets: {formatResetDate(balance.reset_date)}</span>
              </div>
              <Progress value={usagePercent} className="h-1.5" />
            </div>

            {isLowCredits && (
              <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-xs">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>Low credits - Get more power</span>
              </div>
            )}
            
            <Button 
              size="sm" 
              className="w-full btn-oracle"
              onClick={() => window.location.href = '/settings?tab=billing'}
            >
              <Zap className="w-4 h-4 mr-1" />
              Get Credits
            </Button>
          </>
        )}
      </div>
    );
  }

  // Full view
  return (
    <div className="space-y-6">
      {/* Balance Card */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-purple-500/5 to-zinc-900 border border-zinc-800 rounded-2xl p-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                <Coins className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Credit Balance</h3>
                <p className="text-sm text-zinc-500">Power your advanced features</p>
              </div>
            </div>
            
            {balance.is_founder && (
              <div className="px-4 py-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-full flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-400" />
                <span className="text-amber-400 font-medium">Founder Access</span>
              </div>
            )}
          </div>
          
          <div className="flex items-end gap-2 mb-6">
            {balance.is_founder ? (
              <span className="text-4xl font-bold text-primary">Unlimited</span>
            ) : (
              <>
                <span className="text-5xl font-bold text-primary">{balance.balance.toLocaleString()}</span>
                <span className="text-zinc-500 mb-1">credits</span>
              </>
            )}
          </div>

          {!balance.is_founder && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-zinc-900/60 rounded-xl p-3">
                <p className="text-xs text-zinc-500 mb-1">Monthly Allocation</p>
                <p className="text-lg font-semibold">{balance.monthly_credits}</p>
              </div>
              <div className="bg-zinc-900/60 rounded-xl p-3">
                <p className="text-xs text-zinc-500 mb-1">Used This Month</p>
                <p className="text-lg font-semibold">{balance.monthly_used}</p>
              </div>
              <div className="bg-zinc-900/60 rounded-xl p-3">
                <p className="text-xs text-zinc-500 mb-1">Purchased Credits</p>
                <p className="text-lg font-semibold text-green-400">{balance.purchased_credits}</p>
              </div>
              <div className="bg-zinc-900/60 rounded-xl p-3">
                <div className="flex items-center gap-1 text-xs text-zinc-500 mb-1">
                  <Clock className="w-3 h-3" />
                  <span>Resets In</span>
                </div>
                <p className="text-lg font-semibold">{formatResetDate(balance.reset_date)}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Usage Progress */}
      {!balance.is_founder && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="font-medium">Monthly Usage</span>
            <span className="text-sm text-zinc-500">
              {balance.monthly_remaining} of {balance.monthly_credits} remaining
            </span>
          </div>
          <Progress value={usagePercent} className="h-2 mb-4" />
          
          {/* Usage breakdown */}
          {Object.keys(balance.usage_by_feature || {}).length > 0 && (
            <div className="space-y-2 pt-4 border-t border-zinc-800">
              <p className="text-sm text-zinc-500 mb-3">Usage by Feature</p>
              {Object.entries(balance.usage_by_feature).map(([feature, credits]) => {
                const Icon = FEATURE_ICONS[feature] || Sparkles;
                return (
                  <div key={feature} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-zinc-500" />
                      <span>{feature}</span>
                    </div>
                    <span className="text-zinc-400">{credits} credits</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Credit Packs */}
      {!balance.is_founder && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Package className="w-5 h-5 text-primary" />
              Credit Packs
            </h3>
            <span className="text-sm text-zinc-500">Purchased credits never expire</span>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(packs).map(([packId, pack]) => (
              <div 
                key={packId}
                className="relative bg-zinc-900/60 border border-zinc-800 rounded-xl p-5 hover:border-primary/50 transition-all group"
              >
                {packId === 'pack_1500' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary text-white text-xs font-medium rounded-full">
                    Popular
                  </div>
                )}
                
                <div className="text-center mb-4">
                  <p className="text-3xl font-bold text-primary">{pack.credits.toLocaleString()}</p>
                  <p className="text-sm text-zinc-500">credits</p>
                </div>
                
                <div className="text-center mb-4">
                  <span className="text-2xl font-bold">${pack.price}</span>
                  <p className="text-xs text-zinc-500 mt-1">
                    ${(pack.price / pack.credits * 100).toFixed(2)} per 100 credits
                  </p>
                </div>
                
                <Button
                  className="w-full btn-oracle"
                  onClick={() => handlePurchase(packId)}
                  disabled={purchasing === packId}
                >
                  {purchasing === packId ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Get {pack.name}
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Low Credit Warning */}
      {isLowCredits && (
        <div className="flex items-center gap-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-amber-400">Running Low on Credits</p>
            <p className="text-sm text-zinc-400">
              Get more credits to continue using advanced features without interruption.
            </p>
          </div>
          <Button 
            className="btn-oracle"
            onClick={() => handlePurchase('pack_1500')}
          >
            Get Credits
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default CreditDashboard;
